import React, { useState, useEffect } from "react";
import {
  UserGroupIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import { userService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import toast from "react-hot-toast";

function ReferralsPage() {
  const { user } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadReferralData();
  }, []);

  const loadReferralData = async () => {
    try {
      setIsLoading(true);
      const response = await userService.getReferrals();

      if (response.data.success) {
        setReferralData(response.data.data);
      }
    } catch (error) {
      toast.error("Referans verileri yüklenirken bir hata oluştu");
    } finally {
      setIsLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = referralData?.referralUrl;
    if (link) {
      navigator.clipboard.writeText(link);
      toast.success("Referans linki kopyalandı!");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Referans verileri yükleniyor..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Referans Sistemi</h1>
        <p className="text-gray-600">
          Arkadaşlarınızı davet edin ve komisyon kazanın
        </p>
      </div>

      {/* Referral Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card bg-blue-50 border-blue-200">
          <div className="card-body text-center">
            <UserGroupIcon className="w-12 h-12 text-blue-600 mx-auto mb-3" />
            <p className="text-2xl font-bold text-blue-600">
              {referralData?.totalReferrals || 0}
            </p>
            <p className="text-sm text-gray-600">Toplam Referans</p>
          </div>
        </div>

        <div className="card bg-green-50 border-green-200">
          <div className="card-body text-center">
            <UserGroupIcon className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <p className="text-2xl font-bold text-green-600">
              {referralData?.activeReferrals || 0}
            </p>
            <p className="text-sm text-gray-600">Aktif Referans</p>
          </div>
        </div>

        <div className="card bg-yellow-50 border-yellow-200">
          <div className="card-body text-center">
            <span className="text-2xl mb-3 block">💰</span>
            <p className="text-2xl font-bold text-yellow-600">
              ${referralData?.totalReferralEarnings?.toFixed(2) || "0.00"}
            </p>
            <p className="text-sm text-gray-600">Komisyon Kazancı</p>
          </div>
        </div>
      </div>

      {/* Referral Link */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">
            Referans Linkiniz
          </h2>
        </div>
        <div className="card-body">
          <div className="flex items-center space-x-4">
            <input
              type="text"
              value={referralData?.referralUrl || ""}
              readOnly
              className="form-input flex-1 bg-gray-50"
            />
            <button onClick={copyReferralLink} className="btn btn-primary">
              <ClipboardDocumentIcon className="w-4 h-4 mr-2" />
              Kopyala
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Bu linki arkadaşlarınızla paylaşın ve onların kazançlarından %10
            komisyon alın
          </p>
        </div>
      </div>

      {/* Referrals List */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Referanslarım</h2>
        </div>
        <div className="card-body">
          {referralData?.referrals?.length > 0 ? (
            <div className="space-y-3">
              {referralData.referrals.map((referral) => (
                <div
                  key={referral.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {referral.email}
                    </p>
                    <p className="text-sm text-gray-500">
                      Katılım:{" "}
                      {new Date(referral.joinedAt).toLocaleDateString("tr-TR")}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-green-600">
                      +${referral.commissionEarned?.toFixed(2)}
                    </p>
                    <span
                      className={`badge ${referral.isActive ? "badge-success" : "badge-gray"}`}
                    >
                      {referral.isActive ? "Aktif" : "Pasif"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <UserGroupIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">Henüz referansınız bulunmuyor</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReferralsPage;
